import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit3, Trash2, PlusSquare, Building2, BadgeCheck, LayoutList, Grid, MapPin, Search } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatPrice, asItems, formatApiError, internalPropertiesEndpointForRole, markPropertySold } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { canMarkSoldStatus } from "@/lib/propertyUi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const TABS = [
  { id: "all", label: "All Listings" },
  { id: "draft", label: "Draft" },
  { id: "pending", label: "Pending" },
  { id: "published", label: "Published" },
  { id: "sold", label: "Sold" },
];

function TableSkeleton() {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm animate-pulse">
      <div className="h-12 bg-secondary/30 border-b border-border" />
      <div className="divide-y divide-border/60">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 w-1/3">
              <div className="w-14 h-14 rounded-xl bg-muted shrink-0" />
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-muted rounded-md w-3/4" />
                <div className="h-3 bg-muted rounded-md w-1/2" />
              </div>
            </div>
            <div className="h-4 bg-muted rounded-md w-20" />
            <div className="h-4 bg-muted rounded-md w-24" />
            <div className="h-6 bg-muted rounded-full w-20" />
            <div className="h-4 bg-muted rounded-md w-8" />
            <div className="h-8 bg-muted rounded-xl w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="border border-border bg-card rounded-2xl overflow-hidden shadow-sm flex flex-col justify-between h-[320px] animate-pulse">
          <div>
            <div className="w-full h-44 bg-muted" />
            <div className="p-4 space-y-3">
              <div className="flex justify-between">
                <div className="h-3 bg-muted rounded w-16" />
                <div className="h-3 bg-muted rounded w-20" />
              </div>
              <div className="h-5 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </div>
          <div className="p-4 border-t border-border flex justify-between bg-secondary/10">
            <div className="h-6 bg-muted rounded w-24" />
            <div className="h-8 bg-muted rounded w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyProperties() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statsError, setStatsError] = useState("");
  const [confirmAction, setConfirmAction] = useState({ open: false, type: "", id: "" });
  
  // Rich dashboard panel states
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("status")?.toUpperCase();
    if (requested === "DRAFT") return "draft";
    if (["PENDING_VERIFICATION", "UNDER_REVIEW"].includes(requested)) return "pending";
    if (requested === "PUBLISHED") return "published";
    if (["SOLD", "PENDING_SOLD_VERIFICATION"].includes(requested)) return "sold";
    return "all";
  });
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [viewMode, setViewMode] = useState("list"); // "list" or "grid"
  
  const [counts, setCounts] = useState({
    all: 0,
    draft: 0,
    pending: 0,
    published: 0,
    sold: 0,
  });

  const limit = 10;

  // Load status statistics for tabs
  const loadStats = () => {
    if (!user) return;
    setStatsError("");
    api.get("/dashboard/stats")
      .then(({ data }) => {
        const statsList = asItems(data.properties || data.property_status || []);
        const c = { all: 0, draft: 0, pending: 0, published: 0, sold: 0 };
        statsList.forEach(item => {
          const status = String(item.status || "").toUpperCase();
          const count = Number(item.count || 0);
          c.all += count;
          if (status === "DRAFT") c.draft += count;
          if (status === "PENDING_VERIFICATION" || status === "UNDER_REVIEW") c.pending += count;
          if (status === "PUBLISHED" || status === "ACTIVE" || status === "APPROVED") c.published += count;
          if (status === "SOLD" || status === "PENDING_SOLD_VERIFICATION") c.sold += count;
        });
        setCounts(c);
      })
      .catch((requestError) => setStatsError(formatApiError(requestError)));
  };

  const load = () => {
    if (!user) return;
    setLoading(true);
    setError("");
    const endpoint = internalPropertiesEndpointForRole(user?.role);
    
    // Build query params
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("page", String(page));
    params.set("sort", sort);
    if (category !== "all") params.set("category", category);
    if (search.trim() !== "") params.set("q", search);
    
    // Map activeTab to status
    if (activeTab === "draft") params.set("status", "DRAFT");
    else if (activeTab === "pending") params.set("status", "PENDING_VERIFICATION");
    else if (activeTab === "published") params.set("status", "PUBLISHED");
    else if (activeTab === "sold") params.set("status", "SOLD");

    api.get(`${endpoint}?${params.toString()}`)
      .then(({ data }) => {
        setItems(asItems(data.items || data.properties || []));
        setTotal(data.total || 0);
      })
      .catch((requestError) => {
        setItems([]);
        setTotal(0);
        setError(formatApiError(requestError) || "Listings could not be loaded. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    loadStats();
  }, [user, page, activeTab, category, search, sort]);

  const normalizedRole = String(user?.role || "").toLowerCase().replace("office_user", "office");
  const canAdd = ["buyer", "owner", "agent", "office", "admin", "super_admin"].includes(normalizedRole);
  const canEdit = (status) => ["DRAFT", "REJECTED"].includes(String(status || "").toUpperCase());

  const del = async (id) => {
    try { 
      await api.delete(`/properties/${id}`); 
      toast.success("Deleted"); 
      load(); 
      loadStats();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  const requestSold = async (id) => {
    try {
      await markPropertySold(id, "Marked sold from dashboard");
      const role = String(user?.role || "").toLowerCase().replace("office_user", "office");
      toast.success(role === "admin" ? "Property marked SOLD" : "Sold request sent to admin verification");
      load();
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmSelectedAction = async () => {
    const { type, id } = confirmAction;
    setConfirmAction({ open: false, type: "", id: "" });
    if (type === "delete") await del(id);
    if (type === "sold") await requestSold(id);
  };

  return (
    <div data-testid="my-properties-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">
            {user?.role === "agent" ? "Manage" : user?.role === "office" ? "Moderation" : "All Listings"}
          </p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-primary">
            {user?.role === "agent" ? "My Properties" : "Properties"}
          </h1>
        </div>
        {canAdd && (
          <Button asChild className="font-bold gap-2 w-full sm:w-auto" data-testid="add-property-btn"><Link to="/dashboard/property/new?type=land"><PlusSquare className="w-4 h-4"/>Add Property</Link></Button>
        )}
      </div>

      {statsError && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 sm:flex-row sm:items-center sm:justify-between" role="alert">
          <p className="text-xs text-muted-foreground">Listing totals are temporarily unavailable. {statsError}</p>
          <Button type="button" size="sm" variant="outline" onClick={loadStats}>Retry totals</Button>
        </div>
      )}

      {/* Tabs Selector */}
      <div className="w-full sm:w-fit bg-card border border-border/80 p-1 rounded-xl overflow-x-auto scrollbar-none relative flex gap-1">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setActiveTab(tab.id); setPage(1); }}
              className={`flex-1 sm:flex-none text-center px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                isActive
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`property-filter-tab-${tab.id}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activePropertyTab"
                  className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
              }`}>
                {counts[tab.id] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Control bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3.5 bg-card border border-border p-3.5 sm:p-4 rounded-2xl shadow-sm">
        {/* Search */}
        <div className="relative w-full md:flex-1 min-w-0">
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by title, locality or city..."
            className="pl-10 pr-4 py-2 rounded-xl text-sm w-full bg-background border-border"
          />
          <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-3" />
        </div>

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Category filter */}
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-xl px-2.5 py-2 text-xs font-bold text-muted-foreground focus:text-foreground focus:outline-none h-10 w-full sm:w-[120px]"
          >
            <option value="all">All Types</option>
            <option value="PLOT">Plots</option>
            <option value="LAND">Land</option>
            <option value="HOUSE">House</option>
            <option value="FLAT">Flat</option>
          </select>

          {/* Sort selection */}
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="bg-background border border-border rounded-xl px-2.5 py-2 text-xs font-bold text-muted-foreground focus:text-foreground focus:outline-none h-10 w-full sm:w-[140px]"
          >
            <option value="newest">Sort: Newest</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="most_viewed">Sort: Views</option>
          </select>

          {/* Grid vs List View Toggle */}
          <div className="col-span-2 sm:col-span-1 flex items-center justify-end gap-1 bg-background border border-border p-1 rounded-xl h-10 w-full sm:w-auto">
            <span className="text-[10px] uppercase font-bold text-muted-foreground/60 mr-2 sm:hidden pl-2">Layout View:</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === "list"
                    ? "bg-primary text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="List View"
                aria-label="Show properties as a list"
              >
                <LayoutList className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === "grid"
                    ? "bg-primary text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Grid View"
                aria-label="Show properties as a grid"
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        viewMode === "list" ? <TableSkeleton /> : <GridSkeleton />
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-10 text-center" role="alert">
          <p className="font-display text-xl font-bold text-foreground">Listings could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" onClick={load} className="mt-5" title="Retry loading listings">Try again</Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="border border-dashed border-border rounded-2xl p-16 text-center bg-card animate-fadeIn">
          <span className="inline-flex h-16 w-16 mx-auto mb-4 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary animate-float-soft"><Building2 className="w-8 h-8"/></span>
          <h3 className="font-display text-xl font-bold text-primary">No listings found</h3>
          <p className="text-muted-foreground mt-2 mb-6">
            {canAdd ? "Add your first property to get started." : "No properties in this scope."}
          </p>
          {canAdd && (
            <Button asChild className="font-bold"><Link to="/dashboard/property/new?type=land">Add Property</Link></Button>
          )}
        </Card>
      ) : viewMode === "list" ? (
        <div className="space-y-4">
          {/* Mobile Card Row (Rendered instead of cramped table columns on mobile) */}
          <div className="block md:hidden space-y-3">
            {items.map(p => (
              <Card key={p.property_id} className="p-3 bg-card border border-border rounded-2xl flex gap-3 hover:shadow-sm transition" data-testid={`mobile-row-${p.property_id}`}>
                <div className="w-16 h-16 rounded-xl bg-secondary overflow-hidden shrink-0 shadow-inner">
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[9px] text-muted-foreground bg-muted">No Photo</div>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground">{p.category}</span>
                      <span className="text-[9px] font-mono text-muted-foreground">Views: {p.views || p.view_count || 0}</span>
                    </div>
                    <Link to={`/properties/${p.property_id}`} className="font-display font-bold text-base text-primary hover:underline block truncate leading-snug">
                      {p.title}
                    </Link>
                    <p className="text-[10px] text-muted-foreground">{p.city}</p>
                  </div>
                  
                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-dashed border-border/80">
                    <div className="flex items-center gap-1.5">
                      <span className="text-primary font-black text-sm">{formatPrice(p.price)}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="flex items-center gap-1">
                      {canEdit(p.status) && <Link to={`/dashboard/property/new?edit=${p.property_id}`} className="p-2 hover:bg-primary/10 hover:text-primary text-muted-foreground rounded-xl transition" title="Resume and edit listing" aria-label={`Edit ${p.title}`}><Edit3 className="w-4 h-4"/></Link>}
                      {canMarkSoldStatus(p.status) && (
                        <button type="button" onClick={() => setConfirmAction({ open: true, type: "sold", id: p.property_id })} className="p-2 hover:bg-amber-500/10 hover:text-amber-500 text-muted-foreground rounded-xl transition" title="Mark sold" aria-label={`Mark ${p.title} sold`} data-testid={`mark-sold-mobile-${p.property_id}`}>
                          <BadgeCheck className="w-4 h-4"/>
                        </button>
                      )}
                      <button type="button" onClick={() => setConfirmAction({ open: true, type: "delete", id: p.property_id })} className="p-2 hover:bg-destructive/10 hover:text-destructive text-muted-foreground rounded-xl transition" title="Archive listing" aria-label={`Archive ${p.title}`} data-testid={`del-mobile-${p.property_id}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
            <div className="max-h-[70vh] overflow-auto relative scrollbar-thin">
              <table className="w-full min-w-[760px]">
                <thead className="bg-secondary/90 border-b border-border sticky top-0 z-10 backdrop-blur-md shadow-sm">
                  <tr>
                    {["Property", "Price", "City", "Status", "Views", ""].map(h => (
                      <th key={h} className="text-left px-4 py-3.5 text-xs font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => {
                    const isSold = String(p.status).toUpperCase() === "SOLD";
                    const isPendingSold = String(p.status).toUpperCase() === "PENDING_SOLD_VERIFICATION";
                    return (
                      <tr key={p.property_id} className="border-t border-border/80 hover:bg-muted/10 transition" data-testid={`row-${p.property_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden shrink-0 shadow-inner relative">
                              {p.thumbnail ? (
                                <img src={p.thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground bg-muted">No Photo</div>
                              )}
                              {isSold && (
                                <div className="absolute inset-0 bg-red-600/75 flex items-center justify-center text-[9px] font-black text-white uppercase tracking-widest">
                                  Sold
                                </div>
                              )}
                              {isPendingSold && (
                                <div className="absolute inset-0 bg-amber-600/75 flex items-center justify-center text-[8px] font-black text-white uppercase tracking-tighter text-center">
                                  P. Sold
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Link to={`/properties/${p.property_id}`} className="font-display font-bold text-primary hover:underline line-clamp-1">{p.title}</Link>
                                {isSold && (
                                  <span className="shrink-0 text-[8px] font-black bg-red-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">
                                    Sold
                                  </span>
                                )}
                                {isPendingSold && (
                                  <span className="shrink-0 text-[8px] font-black bg-amber-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">
                                    P. Sold
                                  </span>
                                )}
                              </div>
                              <p className="text-xs uppercase tracking-wider text-muted-foreground">{p.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-primary font-bold tabular-nums">{formatPrice(p.price)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{p.city}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={p.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">{p.views || p.view_count || 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {canEdit(p.status) && <Link to={`/dashboard/property/new?edit=${p.property_id}`} className="p-2 hover:bg-primary/10 hover:text-primary text-muted-foreground rounded-xl transition" title="Resume and edit listing" aria-label={`Edit ${p.title}`}><Edit3 className="w-4 h-4"/></Link>}
                            {canMarkSoldStatus(p.status) && (
                              <button type="button" onClick={() => setConfirmAction({ open: true, type: "sold", id: p.property_id })} className="p-2 hover:bg-amber-500/10 hover:text-amber-500 text-muted-foreground rounded-xl transition" title="Mark sold" aria-label={`Mark ${p.title} sold`} data-testid={`mark-sold-${p.property_id}`}>
                                <BadgeCheck className="w-4 h-4"/>
                              </button>
                            )}
                            <button type="button" onClick={() => setConfirmAction({ open: true, type: "delete", id: p.property_id })} className="p-2 hover:bg-destructive/10 hover:text-destructive text-muted-foreground rounded-xl transition" title="Archive listing" aria-label={`Archive ${p.title}`} data-testid={`del-${p.property_id}`}><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-4 bg-card border border-border rounded-2xl shadow-sm">
            <Pagination
              page={page}
              limit={limit}
              total={total}
              onPageChange={setPage}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-6 animate-fadeIn">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(p => {
              const isSold = String(p.status).toUpperCase() === "SOLD";
              const isPendingSold = String(p.status).toUpperCase() === "PENDING_SOLD_VERIFICATION";
              return (
                <Card key={p.property_id} className="overflow-hidden border border-border bg-card rounded-2xl flex flex-col justify-between hover:shadow-md transition duration-200" data-testid={`grid-card-${p.property_id}`}>
                  <div>
                    {/* Photo Thumbnail */}
                    <div className="w-full h-44 bg-secondary relative overflow-hidden group">
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs bg-muted">No Photo</div>
                      )}
                      <div className="absolute top-3 left-3 z-20">
                        <StatusBadge status={p.status} />
                      </div>
                      {isSold && (
                        <div className="absolute inset-0 bg-red-600/80 flex items-center justify-center shadow-inner backdrop-blur-[1px] z-10">
                          <span className="text-xl font-display font-black text-white uppercase tracking-[0.25em] border-2 border-white px-4 py-1.5 rounded-lg rotate-[-12deg] shadow-lg">
                            SOLD
                          </span>
                        </div>
                      )}
                      {isPendingSold && (
                        <div className="absolute inset-0 bg-amber-600/80 flex items-center justify-center shadow-inner backdrop-blur-[1px] z-10">
                          <span className="text-sm font-display font-black text-white uppercase tracking-wider border border-white px-3 py-1 rounded-md rotate-[-8deg] shadow-md">
                            SOLD (PENDING)
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Body */}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{p.category}</span>
                        <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded text-muted-foreground">Views: {p.views || p.view_count || 0}</span>
                      </div>
                      
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/properties/${p.property_id}`} className="font-display font-bold text-lg text-primary hover:underline block leading-snug truncate">
                          {p.title}
                        </Link>
                        {isSold && (
                          <span className="shrink-0 text-[8px] font-black bg-red-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">
                            Sold
                          </span>
                        )}
                        {isPendingSold && (
                          <span className="shrink-0 text-[8px] font-black bg-amber-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">
                            P. Sold
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{p.city}</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer price & actions */}
                  <div className="p-4 border-t border-border flex items-center justify-between bg-secondary/10">
                    <span className="text-primary font-black text-lg">{formatPrice(p.price)}</span>
                    <div className="flex items-center gap-1.5">
                      {canEdit(p.status) && <Link to={`/dashboard/property/new?edit=${p.property_id}`} className="p-2 hover:bg-primary/10 hover:text-primary text-muted-foreground rounded-xl transition" title="Resume and edit listing" aria-label={`Edit ${p.title}`}><Edit3 className="w-4.5 h-4.5"/></Link>}
                      {canMarkSoldStatus(p.status) && (
                        <button type="button" onClick={() => setConfirmAction({ open: true, type: "sold", id: p.property_id })} className="p-2 hover:bg-amber-500/10 hover:text-amber-500 text-muted-foreground rounded-xl transition" title="Mark Sold" aria-label={`Mark ${p.title} sold`} data-testid={`mark-sold-grid-${p.property_id}`}>
                          <BadgeCheck className="w-4.5 h-4.5"/>
                        </button>
                      )}
                      <button type="button" onClick={() => setConfirmAction({ open: true, type: "delete", id: p.property_id })} className="p-2 hover:bg-destructive/10 hover:text-destructive text-muted-foreground rounded-xl transition" title="Archive listing" aria-label={`Archive ${p.title}`} data-testid={`del-grid-${p.property_id}`}>
                        <Trash2 className="w-4.5 h-4.5"/>
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          <div className="p-4 bg-card border border-border rounded-2xl shadow-sm">
            <Pagination
              page={page}
              limit={limit}
              total={total}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <Dialog open={confirmAction.open} onOpenChange={(open) => !open && setConfirmAction({ open: false, type: "", id: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmAction.type === "sold" ? "Mark property as sold?" : "Archive this listing?"}</DialogTitle>
            <DialogDescription>
              {confirmAction.type === "sold"
                ? "Office and agent requests will be sent for admin verification before the final SOLD state is applied."
                : "The listing will be archived and removed from active inventory. Sold or booked properties cannot be archived."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction({ open: false, type: "", id: "" })}>Cancel</Button>
            <Button variant={confirmAction.type === "delete" ? "destructive" : "default"} onClick={confirmSelectedAction}>
              {confirmAction.type === "sold" ? "Continue" : "Archive listing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
